import { useAtomValue } from "@effect/atom-react";
import { type ReactNode, useEffect, useEffectEvent, useRef, useState } from "react";

import {
  CLOUD_SESSION_EXPIRED_DESCRIPTION,
  CLOUD_SESSION_EXPIRED_TITLE,
  CLOUD_SESSION_RECONNECT_ACTION,
  CLOUD_SESSION_RECONNECTING_DESCRIPTION,
  CLOUD_SESSION_SWITCH_TO_LOCAL_ACTION,
} from "@cmmd-forge/client-runtime";

import { clearCloudAuthFailure, cloudAuthFailureAtom } from "../rpc/wsConnectionState";
import { readRuntimeSurfaceState } from "../environments/runtimeSurface";
import { shouldShowCloudAuthRecovery } from "../lib/cloudSessionRecovery";
import { performDesktopCloudReAuth, switchToLocalBackendMode } from "../lib/desktopCloudAuth";
import { toastManager } from "./ui/toast";
import { resolveCloudSessionExpiredToastModel } from "./CloudSessionExpiredCoordinator.logic";

function buildExpiredDescription(onSwitchToLocal: () => void): ReactNode {
  return (
    <span>
      {CLOUD_SESSION_EXPIRED_DESCRIPTION}{" "}
      <button
        className="cursor-pointer underline underline-offset-2 hover:text-foreground"
        onClick={onSwitchToLocal}
        type="button"
      >
        {CLOUD_SESSION_SWITCH_TO_LOCAL_ACTION}
      </button>
    </span>
  );
}

/**
 * Watches the cloudAuthFailureAtom and shows a persistent error toast when
 * the desktop-cloud bearer session has expired or been revoked.
 *
 * Primary action: "Reconnect to Cloud" -- runs the device-OAuth flow.
 * Secondary: "Switch to Local" -- switches to local backend mode (inline link).
 *
 * Mounted alongside WebSocketConnectionCoordinator in the root route.
 * Only active when surface === "desktop-cloud" and status === "expired".
 */
export function CloudSessionExpiredCoordinator() {
  const authFailureState = useAtomValue(cloudAuthFailureAtom);
  const toastIdRef = useRef<ReturnType<typeof toastManager.add> | null>(null);
  const [reconnecting, setReconnecting] = useState(false);
  const [reconnectError, setReconnectError] = useState<string | null>(null);

  const handleReconnect = useEffectEvent(async () => {
    if (reconnecting) return;
    setReconnecting(true);
    setReconnectError(null);
    const abortController = new AbortController();
    try {
      await performDesktopCloudReAuth(abortController.signal);
      clearCloudAuthFailure();
      // The desktop main process relaunches after setBackendMode.
    } catch (error) {
      setReconnecting(false);
      setReconnectError(error instanceof Error ? error.message : "Cloud reconnect failed.");
      // Error is shown via the existing toast; user can retry.
    }
  });

  const handleSwitchToLocal = useEffectEvent(async () => {
    try {
      await switchToLocalBackendMode();
      clearCloudAuthFailure();
      // The desktop main process relaunches.
    } catch {
      // Silently ignore -- bridge unavailable means we can't switch.
    }
  });

  useEffect(() => {
    const runtimeSurface = readRuntimeSurfaceState();
    const shouldShow = shouldShowCloudAuthRecovery(authFailureState, runtimeSurface);
    const toastModel = resolveCloudSessionExpiredToastModel({
      shouldShow,
      reconnecting,
      reconnectError,
    });

    if (toastModel.kind === "visible") {
      const toastPayload = {
        type: "error" as const,
        title: CLOUD_SESSION_EXPIRED_TITLE,
        description:
          toastModel.description === "reconnecting"
            ? (CLOUD_SESSION_RECONNECTING_DESCRIPTION as ReactNode)
            : (toastModel.errorMessage ??
              buildExpiredDescription(() => void handleSwitchToLocal())),
        timeout: 0,
        data: {
          hideCopyButton: true,
        },
        actionProps:
          toastModel.action === null
            ? undefined
            : {
                children: CLOUD_SESSION_RECONNECT_ACTION,
                onClick: () => void handleReconnect(),
              },
      };

      if (toastIdRef.current) {
        toastManager.update(toastIdRef.current, toastPayload);
      } else {
        toastIdRef.current = toastManager.add(toastPayload);
      }
    } else if (toastIdRef.current) {
      toastManager.close(toastIdRef.current);
      toastIdRef.current = null;
    }
  }, [authFailureState, reconnecting, reconnectError]);

  return null;
}
